import React from "react";
import { 
  FileText, 
  GitBranch, 
  Users, 
  Zap, 
  Target,
  MapPin,
  Brain,
  Workflow
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface Template {
  id: string;
  name: string;
  description: string;
  category: string;
  icon: React.ElementType;
  preview: string;
  nodes: number;
  difficulty: "Beginner" | "Intermediate" | "Advanced";
}

const templates: Template[] = [
  {
    id: "basic-flowchart",
    name: "Basic Flowchart",
    description: "Simple process flow with decision points",
    category: "Process",
    icon: GitBranch,
    preview: "/templates/basic-flowchart.png",
    nodes: 8,
    difficulty: "Beginner",
  },
  {
    id: "user-journey",
    name: "User Journey Map",
    description: "Map user interactions and touchpoints",
    category: "UX Design",
    icon: MapPin,
    preview: "/templates/user-journey.png", 
    nodes: 12,
    difficulty: "Intermediate",
  },
  {
    id: "system-architecture",
    name: "System Architecture",
    description: "Technical system components and relationships",
    category: "Technical",
    icon: Workflow,
    preview: "/templates/system-arch.png",
    nodes: 15,
    difficulty: "Advanced",
  },
  {
    id: "decision-tree",
    name: "Decision Tree",
    description: "Complex decision-making flowchart",
    category: "Analysis",
    icon: Brain,
    preview: "/templates/decision-tree.png",
    nodes: 10,
    difficulty: "Intermediate",
  },
  {
    id: "org-chart",
    name: "Organization Chart",
    description: "Company structure and hierarchy",
    category: "Business",
    icon: Users,
    preview: "/templates/org-chart.png",
    nodes: 20,
    difficulty: "Beginner",
  },
  {
    id: "project-flow",
    name: "Project Workflow",
    description: "Project phases and milestones",
    category: "Project Management",
    icon: Target,
    preview: "/templates/project-flow.png",
    nodes: 14,
    difficulty: "Intermediate",
  },
];

interface TemplatesGalleryProps {
  onSelectTemplate: (template: Template) => void;
}

export function TemplatesGallery({ onSelectTemplate }: TemplatesGalleryProps) {
  const getDifficultyColor = (difficulty: Template["difficulty"]) => {
    switch (difficulty) {
      case "Beginner": return "bg-green-100 text-green-800";
      case "Intermediate": return "bg-yellow-100 text-yellow-800"; 
      case "Advanced": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2">Template Gallery</h2>
        <p className="text-muted-foreground">
          Start with a professional template and customize to your needs
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template) => (
          <Card key={template.id} className="hover:shadow-lg transition-shadow cursor-pointer group">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <template.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{template.name}</CardTitle>
                    <CardDescription className="text-xs text-muted-foreground">
                      {template.category}
                    </CardDescription>
                  </div>
                </div>
                <Badge 
                  variant="secondary" 
                  className={`text-xs ${getDifficultyColor(template.difficulty)}`}
                >
                  {template.difficulty}
                </Badge>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-4">
              {/* Preview Image Placeholder */}
              <div className="w-full h-32 bg-muted rounded-lg flex items-center justify-center">
                <template.icon className="w-8 h-8 text-muted-foreground" />
              </div>
              
              <div>
                <p className="text-sm text-muted-foreground mb-3">
                  {template.description}
                </p>
                
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-4">
                  <span>{template.nodes} nodes</span>
                  <span>{template.category}</span>
                </div>
                
                <Button 
                  onClick={() => onSelectTemplate(template)}
                  className="w-full group-hover:bg-primary/90"
                >
                  Use Template
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}